import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { dashboard, opportunities, proposals, getStoredUser } from "@/lib/api";

const monthName = (d) => d.toLocaleString("en-US", { month: "short" }).toUpperCase();

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [opps, setOpps] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const user = getStoredUser();

  useEffect(() => {
    (async () => {
      const [s, o, props] = await Promise.all([
        dashboard.stats(),
        opportunities.list(),
        proposals.list().catch(() => []),
      ]);
      setStats(s);
      setOpps(o);
      // In-progress proposals (not yet submitted), most-recently-updated first.
      const d = props
        .filter((p) => (p.status || "draft") !== "submitted")
        .sort((a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""))
        .slice(0, 6);
      setDrafts(d);
    })();
  }, []);

  const draftPct = (p) => {
    const secs = p.sections || [];
    if (!secs.length) return 0;
    return Math.round(secs.reduce((a, s) => a + (s.percent || 0), 0) / secs.length);
  };

  if (!stats) {
    return (
      <div className="center-load">
        <span className="spinner" /> Loading dashboard…
      </div>
    );
  }

  const chartData = stats.pipeline_6mo.labels.map((l, i) => ({
    month: l,
    Submitted: stats.pipeline_6mo.submitted[i],
    Won: stats.pipeline_6mo.won[i],
  }));

  const upcoming = [...opps]
    .filter((o) => o.due_date)
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .slice(0, 3);
  const urgentCount = upcoming.filter(
    (o) => Math.ceil((new Date(o.due_date) - new Date()) / (1000 * 60 * 60 * 24)) <= 14
  ).length;
  // Real "due this week" count (0-7 days out) — was a hardcoded "2 due this week"
  // shown identically to every org regardless of any actual due date.
  const dueThisWeekCount = opps.filter((o) => {
    if (!o.due_date) return false;
    const days = Math.ceil((new Date(o.due_date) - new Date()) / (1000 * 60 * 60 * 24));
    return days >= 0 && days <= 7;
  }).length;

  const recent = opps.slice(0, 5);

  return (
    <div className="page" data-testid="page-dashboard">
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">
            Welcome back, {user?.name?.split(" ")[0] || "there"} · {dueThisWeekCount} deadline{dueThisWeekCount === 1 ? "" : "s"} this week
          </div>
        </div>
        <div className="flex gap-8">
          <button
            className="btn btn-ghost btn-sm"
            data-testid="dashboard-browse-opps-btn"
            onClick={() => navigate("/opportunities")}
          >
            🔍 Browse Opportunities
          </button>
          <button
            className="btn btn-primary btn-sm"
            data-testid="dashboard-new-response-btn"
            onClick={() => navigate("/new-response")}
          >
            ➕ New Response
          </button>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-card blue" data-testid="stat-active-opps">
          <div className="stat-label">Active Opportunities</div>
          <div className="stat-value">{stats.active_opportunities}</div>
          <div className="stat-icon">📋</div>
        </div>
        <div className="stat-card green" data-testid="stat-submitted">
          <div className="stat-label">Responses Submitted</div>
          <div className="stat-value">{stats.responses_submitted}</div>
          <div className="stat-icon">✅</div>
        </div>
        <div className="stat-card orange" data-testid="stat-pending">
          <div className="stat-label">Pending Responses</div>
          <div className="stat-value">{stats.pending_responses}</div>
          <div className="stat-meta">
            {dueThisWeekCount > 0
              ? <span className="deadline-urgent">{dueThisWeekCount} due this week</span>
              : <span>None due this week</span>}
          </div>
          <div className="stat-icon">⏰</div>
        </div>
        <div className="stat-card purple" data-testid="stat-winrate">
          <div className="stat-label">Win Rate (YTD)</div>
          <div className="stat-value">{stats.win_rate}%</div>
          <div className="stat-icon">🏆</div>
        </div>
      </div>

      {drafts.length > 0 && (
        <div className="card" data-testid="resume-drafts-card">
          <div className="card-header">
            <span className="card-title">Resume a Draft · {drafts.length} in progress</span>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/opportunities")}>View All</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, padding: "14px 20px" }}>
            {drafts.map((p) => {
              const pct = draftPct(p);
              return (
                <div
                  key={p.id}
                  onClick={() => navigate(`/workspace?proposal=${p.id}`)}
                  data-testid={`draft-${p.id}`}
                  style={{ cursor: "pointer", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px" }}
                >
                  <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</span>
                    <span className="pill pill-blue" style={{ flexShrink: 0, marginLeft: 8 }}>{pct}%</span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>
                    {p.agency || "—"}{p.updated_at ? ` · edited ${new Date(p.updated_at).toLocaleDateString()}` : ""}
                  </div>
                  <div className="progress-bar"><div className="progress-fill fill-blue" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid-2">
        <div className="card" data-testid="recent-opps-card">
          <div className="card-header">
            <span className="card-title">Recent Opportunities</span>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/opportunities")}>View All</button>
          </div>
          <div className="opp-list">
            {recent.map((o) => (
              <div
                key={o.id}
                className="opp-row"
                onClick={() => navigate("/opportunities")}
                data-testid={`recent-opp-${o.id}`}
              >
                <span className={`opp-type ${(o.rfp_type || "rfp").toLowerCase()}`}>{o.rfp_type}</span>
                <div className="opp-info">
                  <div className="opp-title">{o.title}</div>
                  <div className="opp-meta">{o.agency} · NAICS {o.naics || "—"}</div>
                </div>
                <span className={`opp-status status-${o.status === "in_review" ? "review" : o.status}`}>
                  {o.status?.replace("_", " ")}
                </span>
                <div className={`opp-deadline ${o.due_date && Math.ceil((new Date(o.due_date) - new Date()) / (1000 * 60 * 60 * 24)) <= 14 ? "deadline-urgent" : ""}`}>
                  {o.due_date ? new Date(o.due_date).toLocaleString("en-US", { month: "short", day: "numeric" }) : "—"}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col" style={{ gap: 16 }}>
          <div className="ai-panel" data-testid="ai-panel">
            <div className="ai-panel-title">🤖 AI Performance Snapshot</div>
            <div className="ai-panel-sub">Powered by GovCo.AI · Claude Sonnet 4.5</div>
            <div className="ai-metric"><span>Avg. Shredding Time</span><span className="ai-metric-val">{stats.ai_metrics.avg_shredding_seconds} sec</span></div>
            <div className="ai-metric"><span>Compliance Coverage</span><span className="ai-metric-val">{stats.ai_metrics.compliance_coverage}%</span></div>
            <div className="ai-metric"><span>Docs Processed</span><span className="ai-metric-val">{stats.ai_metrics.docs_processed}</span></div>
            <div className="ai-metric"><span>Hours Saved (MTD)</span><span className="ai-metric-val">{stats.ai_metrics.hours_saved} hrs</span></div>
          </div>

          <div className="card" data-testid="upcoming-deadlines">
            <div className="card-header">
              <span className="card-title">Upcoming Deadlines</span>
              <span className="pill pill-red">{urgentCount} Urgent</span>
            </div>
            <div className="card-body">
              {upcoming.map((o) => {
                const d = new Date(o.due_date);
                const days = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
                const isUrgent = days <= 14;
                return (
                  <div
                    key={o.id}
                    className={`deadline-card ${isUrgent ? "urgent" : ""}`}
                    data-testid={`deadline-${o.id}`}
                  >
                    <div className="deadline-date-block" style={!isUrgent ? { background: "#546E7A" } : {}}>
                      <div className="deadline-month">{monthName(d)}</div>
                      <div className="deadline-day">{d.getDate()}</div>
                    </div>
                    <div className="deadline-info">
                      <div className="deadline-title">{o.title}</div>
                      <div className="deadline-agency">
                        {o.agency.split(" · ")[0]} · {o.rfp_type} · {days} days left
                      </div>
                    </div>
                    <button
                      className={`btn ${isUrgent ? "btn-warning" : "btn-ghost"} btn-sm`}
                      onClick={() => navigate("/opportunities")}
                    >
                      Open
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="dash-grid">
        <div className="card" data-testid="pipeline-chart-card">
          <div className="card-header">
            <span className="card-title">Pipeline &amp; Win Rate (6 Months)</span>
            <div className="flex gap-8">
              <span className="pill pill-blue">Submitted</span>
              <span className="pill pill-green">Won</span>
            </div>
          </div>
          <div className="card-body">
            <div style={{ height: 180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barCategoryGap="35%">
                  <CartesianGrid stroke="rgba(0,0,0,0.05)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip cursor={{ fill: "rgba(21,101,192,0.06)" }} />
                  <Legend wrapperStyle={{ display: "none" }} />
                  <Bar dataKey="Submitted" fill="rgba(21,101,192,0.85)" radius={[5, 5, 0, 0]} />
                  <Bar dataKey="Won" fill="rgba(46,125,50,0.85)" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Quick Actions</span></div>
          <div className="card-body">
            <div className="quick-actions">
              <button className="quick-action" onClick={() => navigate("/skills")} data-testid="qa-skills">
                <span className="qa-icon">🎖</span> Update Skills
              </button>
              <button className="quick-action" onClick={() => navigate("/past-performance")} data-testid="qa-pp">
                <span className="qa-icon">📋</span> Add Experience
              </button>
              <button className="quick-action" onClick={() => navigate("/opportunities")} data-testid="qa-opps">
                <span className="qa-icon">🔍</span> Browse Opportunities
              </button>
              <button className="quick-action" onClick={() => navigate("/workspace?tab=submission")} data-testid="qa-submission">
                <span className="qa-icon">📦</span> Build Submission
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">Profile Completeness</span></div>
          <div className="card-body">
            {[
              { k: "Skills", v: stats.profile_completeness.skills, fill: "fill-blue" },
              { k: "Experience", v: stats.profile_completeness.experience, fill: "fill-green" },
              { k: "Certifications", v: stats.profile_completeness.certifications, fill: "fill-orange" },
              { k: "Past Performance", v: stats.profile_completeness.past_performance, fill: "fill-purple" },
            ].map((p) => (
              <div className="progress-item" key={p.k}>
                <div className="progress-label"><span>{p.k}</span><span>{p.v}%</span></div>
                <div className="progress-bar">
                  <div className={`progress-fill ${p.fill}`} style={{ width: `${p.v}%` }} />
                </div>
              </div>
            ))}
            <div style={{ marginTop: 14 }}>
              <button
                className="btn btn-primary w-full"
                onClick={() => navigate("/skills")}
                style={{ justifyContent: "center" }}
                data-testid="complete-profile-btn"
              >
                Complete Profile
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
